import React, {useCallback} from 'react'
import {
  Redirect,
  Switch,
  useLocation,
  matchPath,
  useHistory,
  Route,
} from 'react-router-dom'
import {
  BreadcrumbsContextProvider,
  ErrorBoundary,
  useCurrentOrganization,
} from '@therms/react-modules'
import {AppLayoutContainer} from './modules/Layout/shared/AppLayoutContainer'
import {DASHBOARD_ROUTES} from '@src/modules/Dashboard/constants'
import {EVENTS_ROUTES} from '@src/modules/Events/constants'
import {LOCATIONS_ROUTES} from '@src/modules/Locations/constants'
import {RECORDS_ROUTES} from '@src/modules/Records/constants'
import {REPORTS_ROUTES} from '@src/modules/Reports/constants'
import {USERS_ROUTES} from '@src/modules/Users/constants'
import {Loading} from '@therms/atalaya'
import {SCHEDULING_ROUTES} from '@src/modules/Scheduling/constants'
import {MAIL_ROUTES} from '@src/modules/Mail/constants'
import {PATROL_ROUTES} from '@src/modules/Patrol/constants'
import {BULLETINS_ROUTES} from '@src/modules/Bulletins/constants'
import {REVIEW_ROUTES} from '@src/modules/Review/constants'

const BulletinsRoutes = React.lazy(
  () => import('@src/modules/Bulletins/router/Routes'),
)
const ReviewRoutes = React.lazy(
  () => import('@src/modules/Review/router/Routes'),
)
const DashboardRoutes = React.lazy(
  () => import('./modules/Dashboard/router/Routes'),
)
const EventsRoutes = React.lazy(
  () => import('@src/modules/Events/router/Routes'),
)
const LocationsRoutes = React.lazy(
  () => import('./modules/Locations/router/Routes'),
)
const MailRoutes = React.lazy(() => import('@src/modules/Mail/router/Routes'))
const PatrolRoutes = React.lazy(
  () => import('@src/modules/Patrol/router/Routes'),
)
const RecordsRoutes = React.lazy(
  () => import('./modules/Records/router/Routes'),
)
const ReportsRoutes = React.lazy(
  () => import('@src/modules/Reports/router/Routes'),
)
const SchedulingRoutes = React.lazy(
  () => import('@src/modules/Scheduling/router/Routes'),
)
const UsersRoutes = React.lazy(() => import('@src/modules/Users/router/Routes'))

function AppRouter() {
  const history = useHistory()
  const {pathname} = useLocation()
  const linkMatcher = useCallback(
    (link) => !!matchPath(pathname, {exact: true, path: link}),
    [pathname],
  )

  const {
    modules: {scheduling},
  } = useCurrentOrganization()

  return (
    <BreadcrumbsContextProvider
      isCurrentLinkMatcher={linkMatcher}
      onBack={history.goBack}
      onNavigate={history.push}
    >
      <ErrorBoundary onResetError={() => window.location.reload()}>
        <AppLayoutContainer>
          <React.Suspense fallback={<Loading overlay />}>
            <Switch>
              <Route path={REVIEW_ROUTES.__ROOT__} component={ReviewRoutes} />
              <Route
                path={DASHBOARD_ROUTES.__ROOT__}
                component={DashboardRoutes}
              />
              <Route path={EVENTS_ROUTES.__ROOT__} component={EventsRoutes} />
              <Route
                path={LOCATIONS_ROUTES.__ROOT__}
                component={LocationsRoutes}
              />
              <Route path={PATROL_ROUTES.__ROOT__} component={PatrolRoutes} />
              <Route path={RECORDS_ROUTES.__ROOT__} component={RecordsRoutes} />
              <Route path={REPORTS_ROUTES.__ROOT__} component={ReportsRoutes} />
              <Route path={USERS_ROUTES.__ROOT__} component={UsersRoutes} />
              <Route
                path={BULLETINS_ROUTES.__ROOT__}
                component={BulletinsRoutes}
              />
              <Route path={MAIL_ROUTES.__ROOT__} component={MailRoutes} />

              {scheduling && (
                <Route
                  path={SCHEDULING_ROUTES.__ROOT__}
                  component={SchedulingRoutes}
                />
              )}

              <Redirect to={DASHBOARD_ROUTES.__ROOT__} />
            </Switch>
          </React.Suspense>
        </AppLayoutContainer>
      </ErrorBoundary>
    </BreadcrumbsContextProvider>
  )
}

export {AppRouter}
